import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, FileText, AlertTriangle, CheckCircle2, XCircle,
  ListChecks, Loader2, ClipboardList, Building2, Heart,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Layout } from "../components/Layout";
import { RiskBadge, DocTypeBadge, ReviewBadge } from "../components/RiskBadge";

const SEVERITY = {
  low: { cls: "bg-[#E6F0EB] border-[#34745C]/20 text-[#34745C]", icon: CheckCircle2 },
  medium: { cls: "bg-[#FDF3E1] border-[#C48231]/20 text-[#C48231]", icon: AlertTriangle },
  high: { cls: "bg-[#FCEAE8] border-[#B54D40]/20 text-[#B54D40]", icon: AlertTriangle },
};

export default function DocumentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [note, setNote] = useState("");
  const [verifying, setVerifying] = useState(false);

  const load = useCallback(() => api.get(`/documents/${id}`).then((res) => setDoc(res.data)), [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!doc || doc.status !== "processing") return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [doc, load]);

  const verify = async (status) => {
    setVerifying(true);
    try {
      const { data } = await api.post(`/documents/${id}/verify`, { status, note: note || null });
      setDoc(data);
      toast.success(`Document ${status}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
    } finally {
      setVerifying(false);
    }
  };

  if (!doc)
    return (
      <Layout>
        <div className="flex justify-center py-24">
          <Loader2 className="w-8 h-8 text-[#2E5C50] animate-spin" strokeWidth={1.5} />
        </div>
      </Layout>
    );

  const a = doc.analysis;

  return (
    <Layout>
      <div data-testid="document-detail" className="max-w-4xl mx-auto space-y-8">
        <Link to="/dashboard" data-testid="back-to-dashboard" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#57605A] hover:text-[#2E5C50] transition-colors duration-200">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to dashboard
        </Link>

        <div className="bg-white border border-[#E8E6E1] rounded-xl p-6 md:p-8 fade-in-up">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#D6E0D9]/40 rounded-lg flex items-center justify-center shrink-0">
                <FileText className="w-6 h-6 text-[#2E5C50]" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-heading font-semibold break-all">{doc.file_name}</h1>
                <p className="text-sm text-[#57605A] mt-0.5">
                  {doc.client_name && <span>{doc.client_name} · </span>}
                  Uploaded by {doc.uploader_name} · {new Date(doc.created_at).toLocaleString()}
                  {doc.is_demo && <span className="ml-2 text-[#C07A60] font-semibold">DEMO DATA</span>}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <DocTypeBadge type={doc.document_type} />
              <RiskBadge level={doc.risk_level} />
              {doc.status === "analyzed" && <ReviewBadge status={doc.review_status} />}
            </div>
          </div>

          {doc.status === "processing" && (
            <div data-testid="analysis-processing" className="mt-6 flex items-center gap-3 bg-[#FDF3E1] border border-[#C48231]/20 rounded-lg p-4">
              <Loader2 className="w-5 h-5 text-[#C48231] animate-spin" strokeWidth={1.5} />
              <p className="text-sm text-[#1C211F]">Nexari is analyzing this document. This usually takes under a minute…</p>
            </div>
          )}
          {doc.status === "failed" && (
            <div data-testid="analysis-failed" className="mt-6 bg-[#FCEAE8] border border-[#B54D40]/20 rounded-lg p-4">
              <p className="text-sm font-semibold text-[#B54D40]">Analysis failed</p>
              <p className="text-sm text-[#1C211F] mt-1">{doc.error}</p>
            </div>
          )}
        </div>

        {a && (
          <>
            <section className="bg-white border border-[#E8E6E1] rounded-xl p-6 md:p-8 fade-in-up">
              <h2 className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#57605A]">
                <ClipboardList className="w-4 h-4" strokeWidth={1.5} /> Summary
              </h2>
              <p data-testid="analysis-summary" className="mt-3 leading-relaxed text-[#1C211F]">{a.summary}</p>

              {a.extracted_details?.length > 0 && (
                <div className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="extracted-details">
                  {a.extracted_details.map((d, i) => (
                    <div key={i} className="bg-[#F9F7F4] border border-[#E8E6E1] rounded-md px-4 py-3">
                      <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">{d.label}</p>
                      <p className={`mt-0.5 text-sm font-medium ${/missing|expired/i.test(d.value) ? "text-[#B54D40]" : "text-[#1C211F]"}`}>
                        {d.value}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="bg-white border border-[#E8E6E1] rounded-xl p-6 md:p-8 fade-in-up">
              <h2 className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#57605A]">
                <AlertTriangle className="w-4 h-4" strokeWidth={1.5} /> Flags
              </h2>
              {a.flags?.length > 0 ? (
                <div className="mt-4 space-y-3" data-testid="analysis-flags">
                  {a.flags.map((f, i) => {
                    const s = SEVERITY[f.severity] || SEVERITY.medium;
                    return (
                      <div key={i} className={`flex items-start gap-3 border rounded-lg p-4 ${s.cls}`}>
                        <s.icon className="w-5 h-5 shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wider">{f.severity} severity</p>
                          <p className="text-sm text-[#1C211F] mt-0.5">{f.message}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div data-testid="no-flags" className="mt-4 flex items-center gap-3 bg-[#E6F0EB] border border-[#34745C]/20 rounded-lg p-4">
                  <CheckCircle2 className="w-5 h-5 text-[#34745C]" strokeWidth={1.5} />
                  <p className="text-sm text-[#1C211F]">No compliance issues found. This document looks complete and consistent.</p>
                </div>
              )}
            </section>

            <section className="grid md:grid-cols-2 gap-6 fade-in-up">
              <div className="bg-white border border-[#E8E6E1] rounded-xl p-6">
                <h2 className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#57605A]">
                  <Heart className="w-4 h-4" strokeWidth={1.5} /> Next steps — Family
                </h2>
                <ul className="mt-4 space-y-3" data-testid="next-steps-family">
                  {(a.next_steps_family || []).map((s, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed">
                      <ListChecks className="w-4 h-4 text-[#2E5C50] shrink-0 mt-0.5" strokeWidth={1.5} />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white border border-[#E8E6E1] rounded-xl p-6">
                <h2 className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#57605A]">
                  <Building2 className="w-4 h-4" strokeWidth={1.5} /> Next steps — Agency
                </h2>
                <ul className="mt-4 space-y-3" data-testid="next-steps-agency">
                  {(a.next_steps_agency || []).map((s, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed">
                      <ListChecks className="w-4 h-4 text-[#C07A60] shrink-0 mt-0.5" strokeWidth={1.5} />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          </>
        )}

        {(user.role === "agency" || user.role === "admin") && doc.status === "analyzed" && (
          <section className="bg-white border border-[#E8E6E1] rounded-xl p-6 md:p-8 fade-in-up">
            <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#57605A]">Agency verification</h2>
            {doc.review_status !== "pending" ? (
              <div className="mt-4 flex items-start gap-3">
                {doc.review_status === "verified" ? (
                  <CheckCircle2 className="w-5 h-5 text-[#34745C] mt-0.5" strokeWidth={1.5} />
                ) : (
                  <XCircle className="w-5 h-5 text-[#B54D40] mt-0.5" strokeWidth={1.5} />
                )}
                <div>
                  <p className="text-sm font-semibold text-[#1C211F]">
                    {doc.review_status === "verified" ? "Verified" : "Rejected"} by {doc.reviewed_by}
                  </p>
                  {doc.review_note && <p className="text-sm text-[#57605A] mt-1">"{doc.review_note}"</p>}
                </div>
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                <textarea
                  data-testid="review-note-input"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add a note for the family (optional)…"
                  rows={2}
                  className="w-full bg-[#F9F7F4] border border-[#E8E6E1] rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C50]/30 focus:border-[#2E5C50]"
                />
                <div className="flex gap-3">
                  <button
                    data-testid="submit-verification-btn"
                    disabled={verifying}
                    onClick={() => verify("verified")}
                    className="flex items-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-5 py-2.5 text-sm font-medium disabled:opacity-60"
                  >
                    <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /> Verify document
                  </button>
                  <button
                    data-testid="reject-document-btn"
                    disabled={verifying}
                    onClick={() => verify("rejected")}
                    className="flex items-center gap-2 bg-[#FCEAE8] text-[#B54D40] hover:bg-[#F8D8D4] transition-colors duration-200 rounded-md px-5 py-2.5 text-sm font-medium disabled:opacity-60"
                  >
                    <XCircle className="w-4 h-4" strokeWidth={1.5} /> Reject
                  </button>
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </Layout>
  );
}
